/**
 * Which markets are closed to new positions.
 *
 * A market is disabled when either:
 *   - config says so (isMarketDisabledByConfig: all real estate, or `disabled: true`), or
 *   - the perp contract has it closed (`market_trading_state: "closed"`, set via
 *     admin `update_market_trading_state`). The keeper mirrors that into
 *     `perp.borrowing(...).isOpen`, so one aliased GraphQL request covers every
 *     configured market.
 *
 * The on-chain state is fetched once and shared by every component using the
 * hook, refreshed every POLL_MS. Until it arrives (or if the keeper is down)
 * only the config rule applies — the contract still rejects opens on a closed
 * market with "market is closed".
 */

import { useEffect, useState } from 'react';
import { MARKETS, isMarketDisabledByConfig } from '../config/markets';
import { isTestnet } from '../config/network';

const KEEPER_URL = 'https://sai-keeper.nibiru.fi/query';
const POLL_MS = 60000;

let closedMarketIndices = new Set();
let lastFetch = 0;
let inFlight = null;
const listeners = new Set();

const toIndex = (v) => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseInt(String(v).replace(/\D/g, ''), 10);
  return Number.isNaN(n) ? null : n;
};

const fetchClosedMarkets = async () => {
  const collateralId = isTestnet() ? 3 : 1;
  const indices = [...new Set(MARKETS.map((m) => toIndex(m.marketIndex)).filter((n) => n !== null))];
  if (indices.length === 0) return;

  const fields = indices
    .map((i) => `m${i}: borrowing(marketId: ${i}, collateralId: ${collateralId}) { isOpen }`)
    .join('\n');
  const response = await fetch(KEEPER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query: `query MarketOpenState { perp { ${fields} } }` }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const json = await response.json();
  const perp = json?.data?.perp;
  if (!perp) throw new Error('Invalid response structure from GraphQL');

  const closed = new Set();
  for (const i of indices) {
    // A market the keeper doesn't know (null) is left alone, not disabled.
    if (perp[`m${i}`]?.isOpen === false) closed.add(i);
  }
  closedMarketIndices = closed;
  listeners.forEach((fn) => fn(closed));
};

const refresh = () => {
  if (inFlight || Date.now() - lastFetch < POLL_MS) return;
  lastFetch = Date.now();
  inFlight = fetchClosedMarkets()
    .catch((err) => console.error('[useDisabledMarkets] Failed to fetch market state:', err))
    .finally(() => { inFlight = null; });
};

/** True if new positions can't be opened on this market. */
export const isMarketDisabled = (market, closed = closedMarketIndices) => {
  if (!market) return false;
  if (isMarketDisabledByConfig(market)) return true;
  const idx = toIndex(market.marketIndex);
  return idx !== null && closed.has(idx);
};

/**
 * @returns {(market: Object) => boolean} predicate bound to the latest on-chain state
 */
export const useDisabledMarkets = () => {
  const [closed, setClosed] = useState(closedMarketIndices);

  useEffect(() => {
    listeners.add(setClosed);
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    return () => {
      listeners.delete(setClosed);
      window.clearInterval(id);
    };
  }, []);

  return (market) => isMarketDisabled(market, closed);
};

/** Convenience: disabled state of a single market. */
export const useIsMarketDisabled = (market) => useDisabledMarkets()(market);
